import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';

export interface LiveStreamPlayerProps {
  src: string;
  className?: string;
}

const POPUP_NAME = 'sportsbook-live-stream';
const POPUP_WIDTH = 480;
const POPUP_HEIGHT = 270;

/** Survives LiveStreamPlayer remounts when switching live events. */
let sharedStreamPopup: Window | null = null;

function popupFeatures(): string {
  const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - POPUP_WIDTH) / 2));
  const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2));
  return [
    'popup=yes',
    `width=${POPUP_WIDTH}`,
    `height=${POPUP_HEIGHT}`,
    `left=${left}`,
    `top=${top}`,
    'resizable=yes',
    'scrollbars=no',
  ].join(',');
}

function getSharedPopup(): Window | null {
  if (sharedStreamPopup && !sharedStreamPopup.closed) return sharedStreamPopup;
  sharedStreamPopup = null;
  return null;
}

function navigatePopup(win: Window, src: string): Window | null {
  try {
    win.location.href = src;
    return win;
  } catch {
    win.close();
    const next = window.open(src, POPUP_NAME, popupFeatures());
    sharedStreamPopup = next;
    return next;
  }
}

/**
 * Live stream via c-lmt.com strm2.html proxy.
 * Fixed 200px footprint; black surface behind the frame (no edge cropping).
 * Supports popping the stream into a small browser window.
 */
export function LiveStreamPlayer({ src, className }: LiveStreamPlayerProps) {
  const { t } = useTranslation();
  const popupRef = useRef<Window | null>(getSharedPopup());
  const [poppedOut, setPoppedOut] = useState(() => getSharedPopup() != null);

  const clearPopup = useCallback(() => {
    sharedStreamPopup = null;
    popupRef.current = null;
    setPoppedOut(false);
  }, []);

  const closePopup = useCallback(() => {
    const win = popupRef.current ?? getSharedPopup();
    if (win && !win.closed) {
      win.close();
    }
    clearPopup();
  }, [clearPopup]);

  const openPopup = useCallback(() => {
    const existing = getSharedPopup();
    if (existing) {
      const win = navigatePopup(existing, src);
      popupRef.current = win;
      win?.focus();
      setPoppedOut(true);
      return;
    }

    const win = window.open(src, POPUP_NAME, popupFeatures());
    if (!win) return;
    sharedStreamPopup = win;
    popupRef.current = win;
    setPoppedOut(true);
    win.focus();
  }, [src]);

  // Adopt / refresh an already-open popup when the event stream URL changes.
  useEffect(() => {
    const existing = getSharedPopup();
    if (!existing) return;
    const win = navigatePopup(existing, src);
    popupRef.current = win;
    setPoppedOut(Boolean(win && !win.closed));
  }, [src]);

  // Restore inline player when the user closes the popup.
  useEffect(() => {
    if (!poppedOut) return;
    const id = window.setInterval(() => {
      if (!getSharedPopup()) {
        clearPopup();
      }
    }, 400);
    return () => window.clearInterval(id);
  }, [clearPopup, poppedOut]);

  const popOutLabel = t('PopOut', { defaultValue: 'Pop out' });
  const dockLabel = t('Dock', { defaultValue: 'Dock' });
  const poppedOutMessage = t('LiveStreamPoppedOut', {
    defaultValue: 'Live stream is in another window',
  });

  if (poppedOut) {
    return (
      <div
        data-section="mediaiframe"
        className={cn(
          'relative z-[1] flex h-12 w-full items-center justify-between gap-3 bg-black px-3',
          className,
        )}
      >
        <p className="min-w-0 truncate text-[12px] text-white/80">{poppedOutMessage}</p>
        <button
          type="button"
          onClick={closePopup}
          className="shrink-0 rounded-sm border border-white/24 px-2 py-1 text-[11px] font-bold text-white hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
        >
          {dockLabel}
        </button>
      </div>
    );
  }

  return (
    <div
      data-section="mediaiframe"
      className={cn(
        'relative z-[1] isolate h-[200px] w-full overflow-hidden bg-black leading-none',
        className,
      )}
      onTouchStart={(event) => event.stopPropagation()}
      onTouchMove={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        onClick={openPopup}
        aria-label={popOutLabel}
        title={popOutLabel}
        className="absolute top-2 right-2 z-[2] flex size-7 items-center justify-center rounded-md border border-white/24 bg-black/60 text-white backdrop-blur-sm hover:bg-black/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
      >
        <PopOutGlyph />
      </button>
      <iframe
        id="ifrmMedia"
        src={src}
        title="Live stream"
        scrolling="no"
        allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
        className="pointer-events-auto relative z-[1] block h-full w-full touch-manipulation overflow-hidden border-0 bg-black outline-none"
        allowFullScreen
      />
    </div>
  );
}

function PopOutGlyph() {
  return (
    <svg viewBox="0 0 12 12" className="size-3.5" aria-hidden>
      <rect
        x="1.5"
        y="3.5"
        width="7"
        height="7"
        rx="0.75"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.1"
      />
      <path
        d="M5.5 1.5H10.5V6.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.25 1.75L5.5 6.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeLinecap="round"
      />
    </svg>
  );
}
